import { useState } from 'react';
import { Loader2, MessageSquare, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { StarRating } from '@/components/ui/star-rating';
import { form_error_message } from '@/lib/forms';
import { use_vendor_reviews, use_reply_to_review } from '@/features/review/hooks';
import type { VendorReview } from '@/features/review/types';
import { date_label } from '@/lib/datetime';

function fmt(iso: string): string {
  return date_label(iso);
}

/** Per-review reply: shows the existing reply (editable) or a composer. */
function ReplyBox({ review }: { review: VendorReview }) {
  const [open, set_open] = useState(false);
  const [text, set_text] = useState(review.vendor_reply ?? '');
  const reply_m = use_reply_to_review();

  if (review.vendor_reply && !open) {
    return (
      <div className="ml-3 rounded-md border-l-2 border-primary/40 bg-secondary/40 p-2.5 text-sm">
        <p className="mb-0.5 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
          <MessageSquare className="h-3 w-3" /> Your response
        </p>
        <p className="whitespace-pre-wrap">{review.vendor_reply}</p>
        <button
          type="button"
          onClick={() => set_open(true)}
          className="mt-1 text-xs font-semibold text-primary hover:underline"
          data-testid={`edit-reply-${review.id}`}
        >
          Edit
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="self-start" onClick={() => set_open(true)} data-testid={`reply-${review.id}`}>
        <MessageSquare className="h-4 w-4" /> Reply
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <textarea
        value={text}
        maxLength={1000}
        rows={2}
        onChange={(e) => set_text(e.target.value)}
        placeholder="Write a public reply…"
        data-testid={`reply-input-${review.id}`}
        className="w-full resize-none rounded-input border-2 border-input bg-background p-2.5 text-sm outline-none transition-colors focus:border-primary/60"
      />
      {reply_m.error && <p className="text-sm text-destructive">{form_error_message(reply_m.error, 'Could not post reply.')}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="gradient"
          disabled={!text.trim() || reply_m.isPending}
          onClick={async () => {
            await reply_m.mutateAsync({ review_id: review.id, reply: text.trim() });
            set_open(false);
          }}
          data-testid={`reply-submit-${review.id}`}
        >
          {reply_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post reply'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            set_open(false);
            set_text(review.vendor_reply ?? '');
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function VendorReviewsPage() {
  const q = use_vendor_reviews();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Reviews" description="What your customers are saying — reply to build trust." />

      {q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : q.isError ? (
        <ErrorState data-testid="vendor-reviews-error" title="Couldn't load reviews" description={form_error_message(q.error, 'Please try again.')} />
      ) : items.length === 0 ? (
        <EmptyState
          data-testid="vendor-reviews-empty"
          icon={Star}
          title="No reviews yet"
          description="Once customers review your completed bookings, they'll appear here."
        />
      ) : (
        <Card>
          <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="vendor-reviews-list">
            {items.map((r) => (
              <li key={r.id} className="flex flex-col gap-2 p-4" data-testid={`vendor-review-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StarRating value={r.rating} size="sm" />
                    <span className="text-sm font-semibold">{r.reviewer_name}</span>
                    {r.pet_name && <span className="text-xs text-muted-foreground">for {r.pet_name}</span>}
                    {r.is_hidden && <StatusPill tone="destructive">Hidden</StatusPill>}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                </div>
                {r.comment && <p className="whitespace-pre-wrap text-sm">{r.comment}</p>}
                <ReplyBox review={r} />
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="p-3">
              <Button variant="outline" size="sm" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage} data-testid="vendor-reviews-load-more">
                {q.isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load more'}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
